import React, { useState } from 'react';
import { formatCep, formatCpfCnpj, lookupCep, normalizePhone, normalizeUf, UF_OPTIONS } from '../utils/billingFormat';

const BillingFields = ({ billing, setBilling, className = '' }) => {
  const [cepLoading, setCepLoading] = useState(false);
  const [cepHint, setCepHint] = useState('');
  const setAddress = (field, value) => setBilling((prev) => ({
    ...prev,
    address: { ...prev.address, [field]: value },
  }));

  const handleCepBlur = async () => {
    const formatted = formatCep(billing?.address?.zipcode);
    setAddress('zipcode', formatted);
    if (formatted.replace(/\D/g, '').length !== 8) return;
    setCepLoading(true);
    setCepHint('');
    try {
      const result = await lookupCep(formatted);
      if (!result) {
        setCepHint('CEP não encontrado. Você pode preencher o endereço manualmente.');
        return;
      }
      setBilling((prev) => ({
        ...prev,
        address: {
          ...prev.address,
          zipcode: result.zipcode || prev.address.zipcode,
          street: result.street || prev.address.street,
          neighborhood: result.neighborhood || prev.address.neighborhood,
          city: result.city || prev.address.city,
          state: result.state || prev.address.state,
        },
      }));
      setCepHint('Endereço preenchido pelo CEP. Confira o número e os demais dados.');
    } catch {
      setCepHint('Não foi possível consultar o CEP agora. Você pode preencher manualmente.');
    } finally {
      setCepLoading(false);
    }
  };

  return (
    <div className={className}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <label className="text-sm font-medium text-ink">CPF/CNPJ
          <input
            required
            inputMode="numeric"
            autoComplete="off"
            value={billing.taxID}
            onChange={(e) => setBilling((p) => ({ ...p, taxID: e.target.value }))}
            onBlur={() => setBilling((p) => ({ ...p, taxID: formatCpfCnpj(p.taxID) }))}
            placeholder="000.000.000-00"
            className="input-field mt-2"
          />
        </label>
        <label className="text-sm font-medium text-ink">Telefone com DDI
          <input
            required
            type="tel"
            autoComplete="tel"
            value={billing.phone}
            onFocus={() => { if (!billing.phone) setBilling((p) => ({ ...p, phone: '+55 ' })); }}
            onChange={(e) => setBilling((p) => ({ ...p, phone: e.target.value }))}
            onBlur={() => setBilling((p) => ({ ...p, phone: normalizePhone(p.phone) }))}
            placeholder="+55 (32) 99999-9999"
            className="input-field mt-2"
          />
        </label>
      </div>

      <div className="mt-5">
        <p className="text-sm font-semibold text-ink mb-3">Endereço de cobrança</p>
        <div className="grid grid-cols-1 sm:grid-cols-6 gap-4">
          <label className="sm:col-span-2 text-sm font-medium text-ink">CEP
            <input
              required
              inputMode="numeric"
              autoComplete="postal-code"
              value={billing.address.zipcode}
              onChange={(e) => setAddress('zipcode', formatCep(e.target.value))}
              onBlur={handleCepBlur}
              placeholder="00000-000"
              className="input-field mt-2"
            />
            {cepLoading && <span className="block mt-1 text-[11px] text-muted">Buscando CEP…</span>}
          </label>
          <label className="sm:col-span-4 text-sm font-medium text-ink">Rua
            <input required autoComplete="address-line1" value={billing.address.street} onChange={(e) => setAddress('street', e.target.value)} className="input-field mt-2" />
          </label>
          <label className="sm:col-span-2 text-sm font-medium text-ink">Número
            <input required value={billing.address.number} onChange={(e) => setAddress('number', e.target.value)} className="input-field mt-2" />
          </label>
          <label className="sm:col-span-4 text-sm font-medium text-ink">Complemento
            <input autoComplete="address-line2" value={billing.address.complement} onChange={(e) => setAddress('complement', e.target.value)} className="input-field mt-2" />
          </label>
          <label className="sm:col-span-3 text-sm font-medium text-ink">Bairro
            <input required value={billing.address.neighborhood} onChange={(e) => setAddress('neighborhood', e.target.value)} className="input-field mt-2" />
          </label>
          <label className="sm:col-span-2 text-sm font-medium text-ink">Cidade
            <input required autoComplete="address-level2" value={billing.address.city} onChange={(e) => setAddress('city', e.target.value)} className="input-field mt-2" />
          </label>
          <label className="sm:col-span-1 text-sm font-medium text-ink">UF
            <input
              required
              list="billing-uf-options"
              maxLength={2}
              autoComplete="address-level1"
              value={billing.address.state}
              onChange={(e) => setAddress('state', normalizeUf(e.target.value))}
              placeholder="MG"
              className="input-field mt-2 uppercase"
            />
            <datalist id="billing-uf-options">
              {UF_OPTIONS.map((uf) => <option key={uf} value={uf} />)}
            </datalist>
          </label>
        </div>
        {cepHint && <p className="mt-2 text-[11px] text-muted">{cepHint}</p>}
      </div>
    </div>
  );
};

export default BillingFields;
